import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Card, Table, Tag, Spin,
} from '@/shared/ui/primitives';
import {
  TeamOutlined, HomeOutlined, FlagOutlined, SafetyCertificateOutlined,
  ReloadOutlined, AlertOutlined, CheckCircleOutlined, StopOutlined,
  PlusOutlined, BellOutlined, FileSearchOutlined, CloudDownloadOutlined,
  SearchOutlined,
} from '@/shared/ui/icons';
import { useDashboardStats } from '@/features/ops/hooks/useOps';
import { formatDateTime } from '@/shared/utils/formatters';
import DashboardRangeFilter, { type RangeState } from '@/features/ops/components/DashboardRangeFilter';

const STORAGE_KEY = 'dashboard.rangeState';

function loadInitial(): RangeState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as RangeState;
      if (parsed && parsed.preset) return parsed;
    }
  } catch { /* ignore */ }
  return { preset: 'LAST_7_DAYS' };
}

type StatTone = 'primary' | 'success' | 'warning' | 'danger' | 'info';
const toneBg: Record<StatTone, string> = {
  primary: 'var(--color-primary-soft)',
  success: 'var(--success-soft)',
  warning: 'var(--warning-soft)',
  danger: 'var(--error-soft)',
  info: 'var(--info-soft)',
};
const toneFg: Record<StatTone, string> = {
  primary: 'var(--color-primary-dark)',
  success: '#047857',
  warning: '#B45309',
  danger: '#B91C1C',
  info: '#1D4ED8',
};

function Stat({
  title, value, tone = 'primary', icon, hint, footer, loading,
}: {
  title: string; value: number | string; tone?: StatTone;
  icon?: React.ReactNode; hint?: string; footer?: React.ReactNode; loading?: boolean;
}) {
  return (
    <div className="ds-stat">
      <div className="ds-stat-head">
        <div
          className="ds-stat-ico"
          style={{ background: toneBg[tone], color: toneFg[tone] }}
        >
          {icon}
        </div>
        <span>{title}</span>
      </div>
      <div className="ds-stat-value">{loading ? <Spin size="small" /> : value}</div>
      {hint && <div className="ds-stat-hint">{hint}</div>}
      {footer && <div className="ds-stat-footer">{footer}</div>}
    </div>
  );
}

function SectionTitle({ children, tag }: { children: React.ReactNode; tag?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 28, marginBottom: 12 }}>
      <h2 className="ds-title ds-title-5" style={{ margin: 0 }}>{children}</h2>
      {tag}
    </div>
  );
}

export default function DashboardHome() {
  const { t } = useTranslation();
  const [range, setRange] = useState<RangeState>(loadInitial);

  const queryParams = useMemo(() => {
    if (range.preset === 'CUSTOM') return { preset: 'CUSTOM' as const, from: range.from, to: range.to };
    return { preset: range.preset };
  }, [range]);

  const { data, isLoading, isError, refetch, isFetching } = useDashboardStats(queryParams);

  const users = data?.users;
  const ads = data?.roomAds;
  const wanted = data?.wantedAds;
  const reports = data?.reports;
  const admins = data?.admins;
  const activity = data?.activity ?? { inRange: 0, recent: [] };
  const appliedFrom = data?.range?.from ?? null;
  const appliedTo = data?.range?.to ?? null;
  const actionRequired = reports?.actionRequired ?? 0;

  const handleRangeChange = (next: RangeState) => {
    setRange(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const inRangeBadge = (n: number) => (
    <Tag color="info">+{n} {t('dashboard.range.inRange')}</Tag>
  );

  return (
    <div>
      <div className="ds-page-header">
        <div>
          <h1>{t('dashboard.title')}</h1>
          <div className="ds-page-sub">{t('dashboard.welcome')}</div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
          {t('dashboard.refresh')}
        </Button>
      </div>

      <Card styles={{ body: { padding: 14 } }} style={{ marginBottom: 16 }}>
        <DashboardRangeFilter value={range} onChange={handleRangeChange} appliedFrom={appliedFrom} appliedTo={appliedTo} />
      </Card>

      {/* Quick actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        <Link to="/room-ads/new"><Button type="primary" icon={<PlusOutlined />}>{t('dashboard.quick.newAd')}</Button></Link>
        <Link to="/notifications/create"><Button icon={<BellOutlined />}>{t('dashboard.quick.sendNotification')}</Button></Link>
        <Link to="/users"><Button icon={<SearchOutlined />}>{t('dashboard.quick.findUser')}</Button></Link>
        <Link to="/reports"><Button icon={<AlertOutlined />}>{t('dashboard.quick.openQueue')}</Button></Link>
        <Link to="/audit-logs"><Button icon={<FileSearchOutlined />}>{t('dashboard.quick.auditLog')}</Button></Link>
        <Link to="/import/roomaty"><Button icon={<CloudDownloadOutlined />}>{t('dashboard.quick.import')}</Button></Link>
      </div>

      {isError && <div style={{ marginBottom: 16 }}><Alert type="error" showIcon message={t('dashboard.alerts.failedLoad')} /></div>}

      {actionRequired > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            type="warning"
            showIcon
            icon={<AlertOutlined />}
            message={actionRequired === 1
              ? t('dashboard.alerts.reportsAwaitingOne')
              : t('dashboard.alerts.reportsAwaitingMany', { count: actionRequired })}
            action={<Link to="/reports"><Button size="small">{t('dashboard.alerts.openQueue')}</Button></Link>}
          />
        </div>
      )}

      <SectionTitle tag={<Tag color="default">{t('dashboard.range.lifetime')}</Tag>}>
        {t('dashboard.usersSection')}
      </SectionTitle>
      <div className="ds-row" style={{ gap: 16, marginInline: -8 }}>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.total')} value={users?.total ?? 0} loading={isLoading} tone="primary"
                icon={<TeamOutlined />} footer={inRangeBadge(users?.newInRange ?? 0)} />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.active')} value={users?.active ?? 0} loading={isLoading} tone="success" icon={<CheckCircleOutlined />} />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.suspended')} value={users?.suspended ?? 0} loading={isLoading} tone="danger" icon={<StopOutlined />} />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.range.inRange')} value={users?.newInRange ?? 0} loading={isLoading} tone="info"
                hint={t('dashboard.kpi.new24h', { count: users?.newLast24Hours ?? 0 })} />
        </div>
      </div>

      <SectionTitle tag={<Tag color="default">{t('dashboard.range.lifetime')}</Tag>}>
        {t('dashboard.roomAdsSection')}
      </SectionTitle>
      <div className="ds-row" style={{ gap: 16, marginInline: -8 }}>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.total')} value={ads?.total ?? 0} loading={isLoading} tone="primary"
                icon={<HomeOutlined />} footer={inRangeBadge(ads?.newInRange ?? 0)} />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.published')} value={ads?.published ?? 0} loading={isLoading} tone="success" />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.draftRejected')} value={`${ads?.draft ?? 0} / ${ads?.rejected ?? 0}`} loading={isLoading} tone="warning" />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.featured')} value={ads?.featured ?? 0} loading={isLoading} tone="info" />
        </div>
      </div>

      <div className="ds-row" style={{ gap: 16, marginInline: -8, marginTop: 8 }}>
        <div style={{ flex: '1 1 300px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.wantedTotal')} value={wanted?.total ?? 0} loading={isLoading} tone="primary"
                footer={inRangeBadge(wanted?.newInRange ?? 0)} />
        </div>
        <div style={{ flex: '1 1 300px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.wantedPublished')} value={wanted?.published ?? 0} loading={isLoading} tone="success" />
        </div>
        <div style={{ flex: '1 1 300px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.adminsActiveTotal')} value={`${admins?.active ?? 0} / ${admins?.total ?? 0}`} loading={isLoading} tone="info" icon={<SafetyCertificateOutlined />} />
        </div>
      </div>

      <SectionTitle tag={
        <Tag color={actionRequired > 0 ? 'error' : 'success'}>
          {actionRequired > 0
            ? t('dashboard.alerts.moderationToReview', { count: actionRequired })
            : t('dashboard.alerts.moderationClear')}
        </Tag>
      }>
        {t('dashboard.moderation')}
      </SectionTitle>
      <div className="ds-row" style={{ gap: 16, marginInline: -8 }}>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.pending')} value={reports?.pending ?? 0} loading={isLoading} tone="danger" icon={<FlagOutlined />} />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.underReview')} value={reports?.underReview ?? 0} loading={isLoading} tone="warning" />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.kpi.resolved')} value={reports?.resolved ?? 0} loading={isLoading} tone="success" />
        </div>
        <div style={{ flex: '1 1 220px', padding: 8 }}>
          <Stat title={t('dashboard.range.inRange')} value={reports?.newInRange ?? 0} loading={isLoading} tone="info" />
        </div>
      </div>

      <Card
        style={{ marginTop: 24 }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {t('dashboard.activity.title')}
            <Tag color="info">{activity.inRange} {t('dashboard.range.inRange')}</Tag>
          </div>
        }
        extra={<Link to="/audit-logs"><Button type="link" size="small">{t('dashboard.activity.viewAll')}</Button></Link>}
      >
        <Table
          size="small"
          pagination={false}
          loading={isLoading}
          dataSource={activity.recent ?? []}
          rowKey="id"
          locale={{ emptyText: t('dashboard.activity.empty') }}
          columns={[
            { title: t('dashboard.activity.when'), dataIndex: 'createdAt', width: 180, render: (v: string) => formatDateTime(v) },
            { title: t('dashboard.activity.admin'), dataIndex: 'adminEmail', width: 220, render: (v: string) => v || <Tag>{t('dashboard.activity.system')}</Tag> },
            { title: t('dashboard.activity.action'), dataIndex: 'action', width: 200, render: (v: string) => <Tag color="info">{v}</Tag> },
            { title: t('dashboard.activity.target'), render: (_: any, r: any) => r.targetType ? `${r.targetType}#${r.targetId ?? '—'}` : '—' },
            { title: t('dashboard.activity.summary'), dataIndex: 'summary', ellipsis: true },
          ]}
        />
      </Card>
    </div>
  );
}
